import { isAnyOf } from '@reduxjs/toolkit';
import { listenerMiddleware } from './listener.middleware';
import { RootState } from '../appStore';
import { doctorBffApi } from '@/modules';
import {
  billingLineApi,
  deskPaymentApi,
  deskPaymentCaptureTags,
  deskPaymentObservedBillingTags,
  deskPaymentStateTags,
  findDeskPaymentBillingTransitions,
  isDeskPaymentAbandoned,
  isDeskPaymentCaptured,
  reconcileDeskPayments
} from '@/modules/billing';

/**
 * Feeds the desk-payment registry (`deskPaymentRegistry.slice.ts`) from every read that says
 * something about a card payment. Held here rather than in the surfaces that show those
 * payments, because the registry outlives them: a payment reported by a list the drawer read on
 * its way out still has to reach the registry, and a surface that unmounts in the same tick
 * would never dispatch it.
 */

/**
 * One patient's payments: the only read allowed to adopt, and what picks a payment back up
 * after a reload, in a second tab or at a second desk (see the slice's `adopt` doc).
 */
listenerMiddleware.startListening({
  matcher: deskPaymentApi.endpoints.deskPaymentsByPatient.matchFulfilled,
  effect: (action, api) => {
    // Compare with the cache generation this response replaces. Renewal can write history or
    // flag the acte while leaving its new head live, and a deferred outcome can land after the
    // mutation's own invalidations completed; this list transition is then the only witness.
    const { data: previous } =
      deskPaymentApi.endpoints.deskPaymentsByPatient.select(
        action.meta.arg.originalArgs
      )(api.getOriginalState() as RootState);
    const billingTransitions = findDeskPaymentBillingTransitions(
      previous,
      action.payload
    );
    if (billingTransitions.length > 0) {
      api.dispatch(
        doctorBffApi.util.invalidateTags(
          billingTransitions.flatMap(deskPaymentObservedBillingTags)
        )
      );
    }
    api.dispatch(reconcileDeskPayments(action.payload, true));
  }
});

/**
 * The `POST /refresh` poll — still what advances a payment on Stripe from this browser's own
 * action; webhooks are wired since 3c and can advance the very same row off Stripe's own events,
 * but the browser only learns of that by reading again — run by a dialog's tracker or by the
 * registry's headless refresher.
 *
 * It carries the outcome's invalidations because nothing of ours is mutated when a card is
 * captured on Stripe's side, and because the component that ran the poll cannot be trusted to
 * still be there: the reconciliation below drops a finished payment from the registry, which
 * unmounts the very refresher whose poll reported it, discarding any effect it had scheduled.
 * The mutations below need none of this — they carry their own `invalidatesTags`.
 *
 * Announced on the round that moves the status, never on one that restates it: a payment
 * re-read after its outcome — the outcome dialog's "Refresh payment", a remount — has nothing
 * to tell the caches a previous round has not. So the invariant is that an invalidation exists
 * only for a transition a `/refresh` observed, and a payment already terminal when a surface
 * picks it up produces none; nothing may be arranged around one, and nothing is (the dialog's
 * tracker resumes pending payments only, and the refreshers are filtered on
 * `isLiveDeskPaymentPending`).
 *
 * The reconciliation stays unconditional: it is idempotent, and it is what drops a payment that
 * reached its outcome while this browser was only watching it.
 */
listenerMiddleware.startListening({
  matcher: deskPaymentApi.endpoints.deskPayment.matchFulfilled,
  effect: (action, api) => {
    const payment = action.payload;
    // The state as it was before this response was reduced into it: what the entry last said
    // about the payment, or nothing at all on the round that created it — in which case a
    // terminal result is announced even when nothing moved (a hold re-read by the outcome
    // dialog after its entry expired), accepted: one spare round on a manual action, and the
    // same branch is how a capture that landed between the list read and the first poll is
    // ever announced at all.
    const { data: previous } = deskPaymentApi.endpoints.deskPayment.select(
      action.meta.arg.originalArgs
    )(api.getOriginalState() as RootState);
    if (previous?.status !== payment.status) {
      if (isDeskPaymentCaptured(payment.status)) {
        api.dispatch(
          doctorBffApi.util.invalidateTags(deskPaymentCaptureTags(payment))
        );
      } else if (isDeskPaymentAbandoned(payment.status)) {
        api.dispatch(doctorBffApi.util.invalidateTags(deskPaymentStateTags()));
      }
    }
    api.dispatch(reconcileDeskPayments([payment]));
  }
});

const isDeskPaymentMutation = isAnyOf(
  deskPaymentApi.endpoints.startDeskPayment.matchFulfilled,
  deskPaymentApi.endpoints.cancelDeskPayment.matchFulfilled,
  deskPaymentApi.endpoints.resolveInterventionOutcome.matchFulfilled
);

/**
 * What the desk itself did to a payment. No adoption: a start or a resume is registered by the
 * surface that ran it, which is what makes the payment this browser's in the first place.
 *
 * Together with the poll above this is what keeps the refresher host off a finished intent: the
 * listener runs synchronously on the fulfilled action, while the tracker's unfollow only comes
 * from an effect cleanup on the following render — so the entry is already gone by the time the
 * host is free to mount a refresher on it.
 */
listenerMiddleware.startListening({
  matcher: isDeskPaymentMutation,
  effect: (action, api) => {
    // `startListening` cannot carry a union matcher's payload type through, so it arrives as
    // `unknown`; re-applying the matcher narrows it back to the `DeskPayment` all three
    // mutations return, rather than asserting it.
    if (isDeskPaymentMutation(action)) {
      api.dispatch(reconcileDeskPayments([action.payload]));
    }
  }
});

/**
 * The chase dashboard's page of rows, each carrying the one payment that names its line. It
 * spans the hospital, so it only reports on what is already registered — never adopting another
 * desk's live payment.
 */
listenerMiddleware.startListening({
  matcher: billingLineApi.endpoints.billingDashboard.matchFulfilled,
  effect: (action, api) => {
    api.dispatch(
      reconcileDeskPayments(action.payload.rows.map((row) => row.deskPayment))
    );
  }
});
